import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { GET_APP_STAFF_SUMMARY } from "graphql/query/staff-summary-gql";
import { isStaffSummaryId, parseStaffSummaryEvent } from "../helpers/staff-summary-helper";
import { isStaffManagementForbidden } from "../helpers/staff-management-api-helper";

export const STAFF_SUMMARY_POLL_INTERVAL = 60 * 1000;
const empty = () => ({
    bookedCount: null, checkedInCount: null, difference: null, companies: [], loading: false, error: null
});
const visible = () => document.visibilityState !== "hidden";

const useStaffSummary = ({ eeId, companyId = "", includeCheckedIn = true,
    enabled = true, actorKey }) => {
    const client = useApolloClient();
    const active = enabled === true && isStaffSummaryId(eeId) && isStaffSummaryId(actorKey) &&
        (companyId === "" || isStaffSummaryId(companyId));
    const key = JSON.stringify([actorKey, eeId, companyId, includeCheckedIn]);
    const owner = useRef(null);
    const [snapshot, setSnapshot] = useState(() => ({ key: null, ...empty() }));

    useLayoutEffect(() => {
        if (!active) {
            setSnapshot({ key: null, ...empty() });
            return undefined;
        }

        const context = { key, version: 0, loading: false, subscription: null, settle: null };
        owner.current = context;
        const isCurrent = (version) => owner.current === context && context.version === version;
        const finish = (value) => {
            context.settle?.(value);
            context.settle = null;
        };
        const cancel = () => {
            context.version += 1;
            context.subscription?.unsubscribe();
            context.subscription = null;
            finish(null);
        };
        const publishError = (error, version) => {
            if (!isCurrent(version)) return;
            context.loading = false;
            setSnapshot((current) => ({ key, ...empty(),
                companies: current.key === key && !isStaffManagementForbidden(error) ? current.companies : [], error }));
            finish(null);
        };

        // A fresh observable also recovers after a network error terminates its
        // subscription. While mounted, it remains an active Apollo query so a
        // settled check-in/out mutation can refresh the summary immediately.
        const request = () => {
            if (owner.current !== context) return Promise.resolve(null);
            cancel();
            const version = context.version;
            context.loading = true;
            setSnapshot((current) => ({ key, ...empty(),
                companies: current.key === key ? current.companies : [], loading: true }));
            const pending = new Promise((resolve) => { context.settle = resolve; });
            try {
                const observable = client.watchQuery({
                    query: GET_APP_STAFF_SUMMARY,
                    variables: { id: String(eeId), subcontractorID: companyId === "" ? null : String(companyId),
                        includeCheckedIn },
                    fetchPolicy: "no-cache",
                    errorPolicy: "none",
                    notifyOnNetworkStatusChange: true,
                    context: { queryDeduplication: false }
                });
                context.subscription = observable.subscribe({
                    next: (result) => {
                        if (!isCurrent(version)) return;
                        if (result.loading) {
                            context.loading = true;
                            setSnapshot((current) => ({ key, ...empty(),
                                companies: current.key === key ? current.companies : [], loading: true }));
                            return;
                        }
                        try {
                            if (result.error || result.errors?.length || result.partial) {
                                throw result.error || result.errors?.[0] || new Error("Staff summary response is incomplete");
                            }
                            const data = parseStaffSummaryEvent(result.data?.exhibitionEvent, { eeId, companyId, includeCheckedIn });
                            context.loading = false;
                            setSnapshot({ key, ...data, loading: false, error: null });
                            finish(data);
                        } catch (error) {
                            publishError(error, version);
                        }
                    },
                    error: (error) => publishError(error, version)
                });
            } catch (error) {
                publishError(error, version);
            }
            return pending;
        };
        context.refresh = request;
        request();
        const timer = includeCheckedIn ? window.setInterval(() => {
            if (visible() && !context.loading) request();
        }, STAFF_SUMMARY_POLL_INTERVAL) : null;
        const resume = () => { if (visible()) request(); };
        document.addEventListener("visibilitychange", resume);
        window.addEventListener("focus", resume);

        return () => {
            if (owner.current === context) owner.current = null;
            if (timer !== null) window.clearInterval(timer);
            document.removeEventListener("visibilitychange", resume);
            window.removeEventListener("focus", resume);
            cancel();
        };
    }, [client, active, key, actorKey, eeId, companyId, includeCheckedIn]);

    const refresh = useCallback(() => active && owner.current?.key === key ?
        owner.current.refresh() : Promise.resolve(null), [active, key]);
    // Do not expose an old scope even for the render before layout-effect cleanup.
    const current = active && snapshot.key === key ? snapshot : { ...empty(), loading: active };
    return { bookedCount: current.bookedCount, checkedInCount: current.checkedInCount,
        difference: current.difference, companies: current.companies,
        loading: current.loading, error: current.error, refresh };
};

export default useStaffSummary;
